<template>
    <div class="brekz-header-main__search-wrapper d-flex align-items-center py-2 my-1 z-1000">
        <div class="algolia-search-wrapper">
            <div class="algolia-seach-wrapper__input-wrapper z-1000" :class="{'algolia-seach-wrapper__input-wrapper-mobile': isMobile}"  :id="isMobile ? 'autocomplete_mobile' : 'autocomplete'" />
        </div>
        <button
            class="brekz-header__search-button bg-white py-1 d-none d-lg-block"
            @click="removeSearchBackdrop(); collapseSearch();"
        >
            {{ t("algoliaSearch.searchButton") }}
        </button>
    </div>
</template>


<script setup>
import { useI18n } from 'vue-i18n';
import { h, Fragment, render, onMounted, ref } from 'vue';
import { autocomplete } from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic';
import { algoliaClient, querySuggestionsIndex, deleteOldAlgoliaBrowserLocalStorageCache } from "../../../js/algoliaSettings";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { createPopularProductsPlugin } from "../../../js/algolia-plugins/popular-products";
import { createPopularCategoriesPlugin } from "../../../js/algolia-plugins/popular-categories";
import { createQueryMirrorPlugin } from "../../../js/algolia-plugins/query-mirror";
import { htmlSafeInput } from "../../../js/validation/cleaner";

const { t } = useI18n();

const props = defineProps({
    isMobile: {
        type: Boolean,
        default: false,
    },
})

const autocompletePanelOpen = ref(false);

let autocompleteObject = {
    // This object will be replaced with the Autocomplete instance once it's ready
    refresh: () => {
        console.warn('Attempted to call refresh(), but the autocomplete instance is not ready yet')
    },

    setQuery: (query) => {
        console.warn('Attempted to call setQuery(), but the autocomplete instance is not ready yet')
    },
}

const pushHistory = (query) => {
    const urlClass = new URL(document.location);

    if (query !== "" && query !== null) {
        urlClass.searchParams.set("query", query);
    } else {
        urlClass.searchParams.delete("query");
    }

    const urlWithoutQuery = urlClass.toString();
    const urlWithQuery = urlClass.toString();

    if (history.pushState) {
        window.history.pushState({ path: urlWithoutQuery }, "", urlWithQuery);
    }
};

const setQueryOnMounted = () => {
    const urlClass = new URL(document.location);
    const query = urlClass.searchParams.get('query');

    EventBus.$emit("search-input-hit", query);
    EventBus.$emit("set-mobile-search-input", query);
}

const createSearchBackdrop = () => {
    if (document.querySelector('.brekz-algolia-backdrop')) {
        // The backdrop is already open

        return;
    }

    const el = document.createElement('section');

    const header = document.querySelector('.brekz-header-main');

    el.classList.add('brekz-algolia-backdrop');

    el.addEventListener("click", () => {
        removeSearchBackdrop();
        collapseSearch();
    })

    header.append(el);

    autocompletePanelOpen.value = true;

    setTimeout(() => el.classList.add('brekz-algolia-backdrop--fade'), 10);
}

const removeSearchBackdrop = () => {
    autocompletePanelOpen.value = false;
    const el = document.querySelector('.brekz-algolia-backdrop');

    if (el) {
        el.remove();
    }
}

const expandSearch = () => {
    const el = document.querySelector('.aa-Panel');

    if (! el) {
        return;
    }

    // Check if the animation was not removed properly last time
    if (el.classList.contains('aa-Panel--active')) {
        el.classList.remove('aa-Panel--active');

        setTimeout(() => el.classList.add('aa-Panel--active'));

        return;
    }

    EventBus.$emit('close-menu');
    el.classList.add('aa-Panel--active')

    autocompletePanelOpen.value = true;
    autocompleteObject.refresh();
}

const collapseSearch = () => {
    autocompletePanelOpen.value = false;
    const el = document.querySelector('.aa-Panel');

    if (! el) {
        return;
    }

    el.classList.remove('aa-Panel--active');
}

EventBus.$on("query-mirror-plugin-submit", () => {
    const inputs = document.querySelectorAll('.aa-Input');
    inputs.forEach((input) => {
        input.blur()
    })
    autocompleteObject.setIsOpen(false);
    collapseSearch();
    removeSearchBackdrop();
});

EventBus.$on("set-mobile-search-input", (query) => {
    const mobileSearchPlaceholder = document.querySelectorAll('.aa-DetachedSearchButtonPlaceholder');
    mobileSearchPlaceholder.forEach((placeholder) => {
        if (!query || query === '') {
            placeholder.innerText = t('algoliaSearch.searchPlaceholder');
            return;
        }
        placeholder.innerText = query;
    })
    setTimeout(() => {
        autocompleteObject.setQuery(query);
    }, 50);
})

const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'RECENT_SEARCH',
    limit: 5,
    transformSource({ source, state }) {
        return {
            ...source,
            templates: {
                header({ state }) {
                    let recentSearchesSection = document.querySelector(`[data-autocomplete-source-id*="recentSearchesPlugin"]`);
                    if (state.query) {
                        if (recentSearchesSection) {
                            recentSearchesSection.style.display = "none"
                        }
                        return null;
                    }

                    if (recentSearchesSection) {
                        recentSearchesSection.style.display = ""
                    }

                    return (
                        <p class="search-bar-history__title">{t("algoliaSearch.searchHistoryTitle")}:</p>
                    );
                },
                item({ state, item }) {
                    if (state.query) {
                        return null;
                    }

                    const onHistoryItemRemoveClick = (event) => {
                        event.stopPropagation();

                        recentSearchesPlugin.data.removeItem(item.id);

                        autocompleteObject.refresh();
                    }

                    // todo: Improve styling to be more accurate to the Figma design
                    return (
                        <section class="search-bar-history__item">
                            <p>{item.label}</p>
                            <svg onClick={onHistoryItemRemoveClick} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 0.21875C3.25391 0.21875 0.21875 3.25391 0.21875 7C0.21875 10.7461 3.25391 13.7812 7 13.7812C10.7461 13.7812 13.7812 10.7461 13.7812 7C13.7812 3.25391 10.7461 0.21875 7 0.21875ZM7 12.4688C3.97852 12.4688 1.53125 10.0215 1.53125 7C1.53125 3.97852 3.97852 1.53125 7 1.53125C10.0215 1.53125 12.4688 3.97852 12.4688 7C12.4688 10.0215 10.0215 12.4688 7 12.4688ZM9.78359 5.29922L8.08281 7L9.78359 8.70078C9.91211 8.8293 9.91211 9.03711 9.78359 9.16563L9.16563 9.78359C9.03711 9.91211 8.8293 9.91211 8.70078 9.78359L7 8.08281L5.29922 9.78359C5.1707 9.91211 4.96289 9.91211 4.83437 9.78359L4.21641 9.16563C4.08789 9.03711 4.08789 8.8293 4.21641 8.70078L5.91719 7L4.21641 5.29922C4.08789 5.1707 4.08789 4.96289 4.21641 4.83437L4.83437 4.21641C4.96289 4.08789 5.1707 4.08789 5.29922 4.21641L7 5.91719L8.70078 4.21641C8.8293 4.08789 9.03711 4.08789 9.16563 4.21641L9.78359 4.83437C9.91211 4.96289 9.91211 5.1707 9.78359 5.29922Z" fill="#B9B9B9"/>
                            </svg>
                        </section>
                    );
                },
            },
            onSelect({ item }) {
                collapseSearch();
                removeSearchBackdrop();
                EventBus.$emit("search-input-hit", item.label);
                EventBus.$emit("set-mobile-search-input", item.label);

                pushHistory(item.label);

                setTimeout(() => {
                    window.scrollTo(0, 0);
                }, 200)
            }
        }
    }
});

const querySuggestionsPlugin = createQuerySuggestionsPlugin({
    searchClient: algoliaClient(),
    indexName: querySuggestionsIndex(),
    getSearchParams({ state }) {
        return { hitsPerPage: 5 };
    },
    transformSource({ source }) {
        return {
            ...source,
            templates: {
                header({ state }) {
                    let querySuggestionsSection = document.querySelector(`[data-autocomplete-source-id*="querySuggestionsPlugin"]`);
                    if (state.query !== "" && state.query !== null) {
                        if (querySuggestionsSection) {
                            querySuggestionsSection.style.display = ""
                        }
                        return (
                            <p class="search-bar-suggestions__title">{t("algoliaSearch.searchResultsTitle")}:</p>
                        );
                    }


                    if (querySuggestionsSection) {
                        querySuggestionsSection.style.display = "none";
                    }

                    return null;
                },
                item({ state, item, components }) {
                    if (state.query !== "" && state.query !== null) {
                        return (
                            <section class="search-bar-suggestions__item" data-query={state.query}>
                                <p>
                                    <components.Highlight hit={item} attribute="query" />
                                </p>
                            </section>
                        );
                    }

                    return null;
                },
                noResults({ state }) {
                    if (state.query !== ""&& state.query !== null) {
                        return (
                            <p className="search-bar-suggestions__item-no-results" v-html={t('algoliaSearch.noResultsWithQuery', { query: htmlSafeInput(state.query) })}></p>
                        );
                    }

                    return null;

                }
            },
            onSelect({ item }) {
                removeSearchBackdrop();
                collapseSearch();
                EventBus.$emit("search-input-hit", item.query);
                EventBus.$emit("set-mobile-search-input", item.query);

                pushHistory(item.query);
            },
        }
    }
});

window.addEventListener('popstate', () => {
    const query = new URL(document.location).searchParams.get('query');
    autocompleteObject.setQuery(query);
    EventBus.$emit("search-input-hit", query);
    EventBus.$emit("set-mobile-search-input", query);
});

const popularProductsPlugin = createPopularProductsPlugin({});
const popularCategoriesPlugin = createPopularCategoriesPlugin({});
const queryMirrorPlugin = createQueryMirrorPlugin({});

onMounted(() => {
    deleteOldAlgoliaBrowserLocalStorageCache();

    autocompleteObject = autocomplete({
        initialState: {
            query: new URL(window.location).searchParams.get('query'),
        },

        container: props.isMobile ?'#autocomplete_mobile' : '#autocomplete',
        plugins: [queryMirrorPlugin, recentSearchesPlugin, querySuggestionsPlugin, popularCategoriesPlugin, popularProductsPlugin],
        placeholder: t('algoliaSearch.searchPlaceholder'),
        openOnFocus: true,
        renderer: { createElement: h, Fragment, render },
        render({ children }, root) {
            render(children, root);
        },
        navigator: {
            navigate({ itemUrl }) {
                window.location.assign(itemUrl);
            },
            navigateNewTab({ itemUrl }) {
                const windowReference = window.open(itemUrl, '_blank', 'noopener');

                if (windowReference) {
                    windowReference.focus();
                }
            },
            navigateNewWindow({ itemUrl }) {
                window.open(itemUrl, '_blank', 'noopener');
            },
        },
        onStateChange({ state }) {
            if (autocompletePanelOpen.value === true) {
                return;
            }

            if (state.isOpen) {
                createSearchBackdrop();

                setTimeout(() => expandSearch(), 10);
            } else {
                removeSearchBackdrop();

                collapseSearch();
            }
        },
        onSubmit({ state }) {
            removeSearchBackdrop();
            collapseSearch();
            EventBus.$emit("search-input-hit", state.query);
            EventBus.$emit("set-mobile-search-input", state.query);

            pushHistory(state.query);
        },
    });

    document.addEventListener("DOMContentLoaded",() => {
        const inputs = document.querySelectorAll('.aa-Input');
        const searchButtonMobile = document.querySelector('.search-bar__block--last');
        const autoCompleteSearchButton = document.querySelectorAll('.aa-DetachedSearchButton')[1];
        const searchBarShowButtonMobile = document.querySelector('.search-bar-show__button--mobile');
        const searchBarCloseButton = document.querySelector('.search-bar__close');
        const searchBarMobile = document.querySelector('.search-bar--mobile');
        const query = new URL(document.location).searchParams.get('query');
        EventBus.$emit("set-mobile-search-input", query);

        setQueryOnMounted();

        if (searchBarShowButtonMobile) {
            searchBarShowButtonMobile.addEventListener('click', () => {
                autoCompleteSearchButton.click();
            })
        }

        if (searchButtonMobile) {
            searchButtonMobile.addEventListener('click', () => {
                autoCompleteSearchButton.click();
            });
        }
        inputs.forEach((input) => {
            input.addEventListener('keyup', (event) => {
                if (event.key === 'Enter') {
                    EventBus.$emit('search-input-hit', input.value);
                }
            })
        })
        if (autoCompleteSearchButton) {
            autoCompleteSearchButton.addEventListener('click', () => {
                setTimeout(() => {
                    const mobileCancelButton = document.querySelector('.aa-DetachedCancelButton');
                    if (mobileCancelButton) {
                        mobileCancelButton.addEventListener('click', () => {
                            if (searchBarCloseButton) {
                                searchBarCloseButton.click();
                            }
                        })
                    }
                }, 50)
            })
        }

        const clearButton = document.querySelectorAll('.aa-InputWrapperSuffix');
        clearButton.forEach((input) => {
            input.addEventListener('click', () => {
                EventBus.$emit('search-input-hit', null);
                pushHistory('');

                document.querySelectorAll('.brekz-header__logo-image')?.forEach((element) => element.focus());
            })
        });

        document.querySelectorAll('.brekz-header__search-button').forEach((button) => {
            button.addEventListener('click', () => {
                if (inputs[0].value) {
                    EventBus.$emit('search-input-hit', inputs[0].value);
                }
            })
        });
    })

    document.addEventListener("keyup", (event) => {
        if (event.key === "Escape") {
            removeSearchBackdrop();
            collapseSearch();
        }
    });
});

</script>

<style>
.z-1000 {
    z-index: 1000;
}

.brekz-algolia-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 951;
    background-color: gray;
    opacity: 0;

    transition: opacity .35s ease-out;
}

.brekz-algolia-backdrop--fade {
    opacity: .25;
}

.aa-Item:empty {
    min-height: 0 !important;
    padding: 0 !important;
}
[aria-controls="autocomplete-0-list"] {
    border-radius: 5px 0 0 0;
}

.aa-ClearButton:focus{
    border: none;
}

.aa-Form:focus-within {
    border: none;
    border-color: white;
    box-shadow: none;
}

.aa-Input {
    height: 34px;
    padding: 4px 8px 4px 8px;
}

.aa-Input:focus {
    outline: none;
}

.aa-Label {
    margin-bottom: 0;
}

.aa-InputWrapper, .aa-InputWrapperPrefix, .aa-InputWrapperSuffix {
    height: 34px;
}

.aa-InputWrapperPrefix {
    display: none;
}

.aa-SubmitIcon {
    display: none;
}

.aa-Panel {
    position: fixed;
    z-index: 1000;
    margin: 0;
    border-radius: 0;
    height: 0;
    max-height: 80vh;
    overflow-y: auto;

    animation: slide-in-animation ease-in .35s;
}

.aa-Panel--active {
    height: auto;
}

@media screen and (min-width: 1024px) {
    .aa-Panel--active {
        top: 72px !important;
    }
}

@media screen and (max-width: 1023px) {
    .aa-Panel--active {
        top: 0 !important;
    }
}

.aa-Panel--scrollable {
    padding: 0 !important;
}
.aa-List:first-child {
    border-top: none;
}
mark {
    padding: 0 0 0 0;
}

@keyframes slide-in-animation {
    0% {
        margin-top: -15px;
        transform-origin: -100% 0;
        transform: scaleY(0);
    }
    50% {
        margin-top: 0;
    }
    65% {
        transform: scaleY(1);
    }
    90% {
        margin-top: -4px;
    }
    100% {
        margin-top: 0;
    }
}


[data-autocomplete-source-id="queryMirrorPlugin"],
[data-autocomplete-source-id="recentSearchesPlugin"]
{
    padding: 15px 25px;
}

[data-autocomplete-source-id="popularProductsPlugin"],
[data-autocomplete-source-id="popularCategoriesPlugin"],
[data-autocomplete-source-id="queryMirrorPlugin"],
[data-autocomplete-source-id="querySuggestionsPlugin"],
[data-autocomplete-source-id="products"]{
    padding: 15px 25px;
    border-top: 1px solid #E5E5E5;
}
</style>
